import { Component, type ReactNode } from "react";
import { AlertTriangle, RotateCcw, Copy } from "lucide-react";
import { captureAppError } from "@/monitoring/sentry";

/**
 * OS-wide error boundary. Without it, an unhandled render error in production
 * silently unmounts the React tree → user sees a black screen with no clue
 * what happened. This boundary catches the error, logs the full stack to the
 * console, reports it to Sentry (with the React component stack) and renders a
 * recoverable UI showing the incident code so the user can quote it to us.
 */

type Props = { children: ReactNode };
type State = { error: Error | null; eventId: string | null };

export class OSErrorBoundary extends Component<Props, State> {
  state: State = { error: null, eventId: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, eventId: null };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // eslint-disable-next-line no-console
    console.error("[OSErrorBoundary] caught render error:", error, info.componentStack);
    const eventId = captureAppError(error, {
      area: "react-render",
      level: "fatal",
      extra: {
        componentStack: info.componentStack,
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
      },
    });
    if (eventId) this.setState({ eventId });
  }

  reset = () => {
    this.setState({ error: null, eventId: null });
  };

  reload = () => {
    if (typeof window !== "undefined") {
      try {
        sessionStorage.removeItem("sc.os.booted");
      } catch {
        /* ignore */
      }
      window.location.reload();
    }
  };

  render() {
    const { error, eventId } = this.state;
    if (!error) return this.props.children;

    const isEs =
      typeof navigator !== "undefined" &&
      (navigator.language || "").toLowerCase().startsWith("es");
    const T = isEs
      ? {
          title: "Algo salió mal",
          body: "Encontramos un error al cargar esta pantalla. Ya nos llegó el reporte automáticamente. Puedes intentar de nuevo o recargar la sesión.",
          retry: "Reintentar",
          reload: "Recargar sesión",
          details: "Detalles técnicos",
          incident: "Código de incidente",
          copy: "Copiar código",
        }
      : {
          title: "Something went wrong",
          body: "We hit an error loading this screen. The report already reached us automatically. You can retry, or reload the session.",
          retry: "Retry",
          reload: "Reload session",
          details: "Technical details",
          incident: "Incident code",
          copy: "Copy code",
        };


    return (
      <div className="min-h-[60vh] w-full flex items-center justify-center p-6">
        <div className="w-full max-w-lg rounded-2xl border bg-card p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-rose-500/10 p-2 text-rose-500">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-medium">{T.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{T.body}</p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={this.reset}
              className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:opacity-90 transition-opacity"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {T.retry}
            </button>
            <button
              onClick={this.reload}
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-foreground/[0.04] transition-colors"
            >
              {T.reload}
            </button>
          </div>

          {eventId && (
            <div className="mt-4 flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs">
              <span className="text-muted-foreground">{T.incident}:</span>
              <code className="font-mono">{eventId.slice(0, 8).toUpperCase()}</code>
              <button
                onClick={() => navigator.clipboard?.writeText(eventId)}
                className="ml-auto inline-flex items-center gap-1 rounded border px-2 py-1 hover:bg-foreground/[0.04] transition-colors"
                title={T.copy}
              >
                <Copy className="h-3 w-3" />
                {T.copy}
              </button>
            </div>
          )}



          <details className="mt-5 rounded-md border bg-muted/30 p-3 text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              {T.details}
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed text-foreground/80">
              {String(error?.message || error)}
              {error?.stack ? `\n\n${error.stack}` : ""}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}